import { useEffect, useRef } from "react"

import { Analytics, useAnonymousId } from "@bounce/analytics"
import { isEmptyObject } from "@bounce/util"
import type { Nullable } from "@bounce/util"

import { Client } from "../client"
import type { UserAttributes } from "../client"
import { setVariants } from "../hooks/useVariants"
import type { FeatureFlagsAppConfig } from "../types"

export type FeatureFlagsProviderProps = {
  config: FeatureFlagsAppConfig["featureFlags"]
  user: Nullable<{
    id: Nullable<string>
    targetingAttributes?: UserAttributes
  }>
}

const FIVE_MINUTES = 5 * 60 * 1000

export const FeatureFlagsProvider = ({ config, user }: FeatureFlagsProviderProps) => {
  const anonymousId = useAnonymousId.useAnonymousId()
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    const userValues = {
      anonymousId,
      realUserId: user?.id,
      targetingAttributes: user?.targetingAttributes,
    }

    const update = async () => {
      // Make sure the values are associated with the user
      const { targetingAttributes } = userValues
      if (targetingAttributes && !isEmptyObject(targetingAttributes)) {
        await Analytics.identify(undefined, targetingAttributes)
      }

      if (!hasInitializedRef.current) {
        await Client.init({ ...config, user: userValues })
        hasInitializedRef.current = true
      } else {
        await Client.updateUser(userValues)
      }

      const allVariants = Client.getAllVariants()
      setVariants(allVariants)
    }

    void update()

    const interval = setInterval(update, FIVE_MINUTES)

    return () => {
      clearInterval(interval)
    }
  }, [config, user, anonymousId])

  return null
}
